import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AdjustmentsHorizontalIcon, MagnifyingGlassIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { HidePaneButton, PaneRail } from '../../search/PaneControls';
import { CAMERA_TAG_COLORS } from '../CameraTagChip';
import {
  PTZ_FILTER_OPTIONS, type CameraTagCatalog, type PtzFilter, type SelectedCameraTag,
} from './cameraTagsApi';

/**
 * The camera list's filter column, on both dashboards — the same shape as the Search & Track
 * page: labelled sections on the left that fold to a rail, the list beside it.
 *
 * Fully controlled. Each app keeps the filter state where it already was (the local app in its
 * cameras slice, the cloud in its page) and passes it in; the group tree arrives as a slot
 * because each app renders its own.
 *
 * Within one tag category ANY selected tag matches; categories, groups and PTZ must ALL match.
 */

export interface CameraFiltersColumnProps {
  open: boolean;
  onToggle: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** The app's group tree (with checkboxes). */
  groupsSlot: ReactNode;
  groupCount: number;
  onClearGroups: () => void;
  catalog: CameraTagCatalog;
  selectedTags: SelectedCameraTag[];
  onToggleTag: (tag: SelectedCameraTag) => void;
  onSetTags: (tags: SelectedCameraTag[]) => void;
  ptz: PtzFilter | string;
  onPtzChange: (value: PtzFilter) => void;
  onClearAll: () => void;
  /** Where tags are managed; the link is shown only when given (i.e. the user may edit). */
  manageTagsHref?: string;
  /** Height classes — the column fills the screen under each app's own header. */
  heightClass?: string;
}

/** One titled block — the Search page's FilterSection, with an optional action on the right. */
function Section({ title, hint = null, action = null, children }: {
  title: string; hint?: ReactNode; action?: ReactNode; children: ReactNode;
}) {
  return (
    <section className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
          {hint && <span className="ml-1.5 font-normal normal-case tracking-normal">{hint}</span>}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

/** A full-width checkable row: box (or radio dot), optional colour swatch, label, count. */
function OptionRow({ checked, onClick, label, count = null, swatch = null, radio = false }: {
  checked: boolean; onClick: () => void; label: string; count?: number | null; swatch?: string | null; radio?: boolean;
}) {
  return (
    <button
      type="button"
      role={radio ? 'radio' : 'checkbox'}
      aria-checked={checked}
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors ${
        checked ? 'bg-brand-light font-medium text-brand' : 'text-foreground hover:bg-muted'
      }`}
    >
      {radio ? (
        <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border ${checked ? 'border-brand' : 'border-border'}`}>
          {checked && <span className="h-1.5 w-1.5 rounded-full bg-brand" />}
        </span>
      ) : (
        <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${checked ? 'border-brand bg-brand text-white' : 'border-border'}`}>
          {checked && <CheckIcon className="h-2.5 w-2.5 stroke-[3]" />}
        </span>
      )}
      {swatch && <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: swatch }} />}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== null && <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{count}</span>}
    </button>
  );
}

export default function CameraFiltersColumn({
  open, onToggle, search, onSearchChange, groupsSlot, groupCount, onClearGroups,
  catalog, selectedTags, onToggleTag, onSetTags, ptz, onPtzChange, onClearAll, manageTagsHref,
  heightClass = 'h-[calc(100vh-5rem)] sm:h-[calc(100vh-5.5rem)] lg:h-[calc(100vh-6.5rem)]',
}: CameraFiltersColumnProps) {
  const selectedTagIds = new Set(selectedTags.map((t) => t.id));
  const activeCount = (search ? 1 : 0) + groupCount + selectedTags.length + (ptz ? 1 : 0);
  const categories = catalog.categories.filter((c) => c.tags.length > 0);

  const clearLink = (onClick: () => void, n?: number) => (
    <button type="button" onClick={onClick} className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
      Clear{n ? ` (${n})` : ''}
    </button>
  );

  return (
    // Width animates between the column and a rail; the controls stay mounted while folded so a
    // half-typed search survives a fold (same as the Search page). Sticky + full height, so it
    // stays beside the list as the list scrolls and scrolls inside itself.
    <div
      className={`sticky top-0 shrink-0 self-start overflow-hidden rounded-lg border border-border bg-card shadow-card transition-[width] duration-300 ease-in-out ${heightClass} ${
        open ? 'w-[16rem] xl:w-[17.5rem]' : 'w-11'
      }`}
    >
      <aside
        aria-label="Camera filters"
        aria-hidden={!open}
        className={`flex h-full w-[16rem] flex-col transition-opacity duration-200 xl:w-[17.5rem] ${open ? 'opacity-100' : 'invisible opacity-0'}`}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-1.5">
          <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <AdjustmentsHorizontalIcon className="h-3.5 w-3.5" />
            Camera filters
          </span>
          <HidePaneButton side="left" label="camera filters" onClick={onToggle} />
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3 py-3" style={{ scrollbarWidth: 'thin' }}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground">Filters</span>
            {activeCount > 0 && clearLink(onClearAll, activeCount)}
          </div>

          <Section title="Name">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search cameras…"
                className="w-full rounded-lg border border-border bg-input py-1.5 pl-8 pr-7 text-[13px] text-foreground placeholder-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring"
              />
              {search && (
                <button type="button" onClick={() => onSearchChange('')} aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded text-muted-foreground hover:text-foreground">
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </Section>

          <Section
            title="Groups"
            hint={groupCount > 0 ? `${groupCount} selected · incl. sub-groups` : 'incl. sub-groups'}
            action={groupCount > 0 ? clearLink(onClearGroups) : null}
          >
            <div className="-mx-1 max-h-64 overflow-y-auto rounded-md border border-border p-1" style={{ scrollbarWidth: 'thin' }}>
              {groupsSlot}
            </div>
          </Section>

          <Section title="Camera type" action={ptz ? clearLink(() => onPtzChange('')) : null}>
            <div role="radiogroup" aria-label="Camera type" className="-mx-1 space-y-0.5">
              {PTZ_FILTER_OPTIONS.map((o) => (
                <OptionRow
                  key={o.value || 'any'}
                  radio
                  checked={(ptz || '') === o.value}
                  onClick={() => onPtzChange(o.value)}
                  label={o.label}
                  count={o.value ? catalog.ptz[o.value] : null}
                />
              ))}
            </div>
          </Section>

          {categories.map((cat) => {
            const picked = cat.tags.filter((t) => selectedTagIds.has(t.id));
            return (
              <Section
                key={cat.id}
                title={cat.name}
                hint={cat.multi_select ? 'any of' : 'one per camera'}
                action={picked.length > 0 ? clearLink(() => onSetTags(selectedTags.filter((t) => t.category_id !== cat.id))) : null}
              >
                <div className="-mx-1 space-y-0.5">
                  {cat.tags.map((t) => (
                    <OptionRow
                      key={t.id}
                      checked={selectedTagIds.has(t.id)}
                      onClick={() => onToggleTag({ id: t.id, name: t.name, color: t.color, category_id: cat.id, category_name: cat.name })}
                      label={t.name}
                      count={t.camera_count ?? 0}
                      swatch={CAMERA_TAG_COLORS[t.color || ''] || CAMERA_TAG_COLORS.slate}
                    />
                  ))}
                </div>
              </Section>
            );
          })}

          {manageTagsHref && (
            <Link to={manageTagsHref} className="block px-1 text-[12px] font-medium text-brand hover:underline">
              {categories.length ? 'Manage camera tags' : 'Create tags such as departments or indoor/outdoor'}
            </Link>
          )}
        </div>
      </aside>

      {/* Folded: a way back in, with the number of filters still narrowing the list. */}
      {!open && (
        <PaneRail
          label="Camera filters"
          Icon={AdjustmentsHorizontalIcon}
          count={activeCount}
          onExpand={onToggle}
          opens="right"
          className="absolute inset-0"
        />
      )}
    </div>
  );
}

/** Toggle one tag in a selection list — what a click in the column means. */
export function toggleSelectedTag(list: SelectedCameraTag[], tag: SelectedCameraTag): SelectedCameraTag[] {
  return list.some((t) => t.id === tag.id) ? list.filter((t) => t.id !== tag.id) : [...list, tag];
}
